import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AnalyticsComponent } from '@lambdatestincprivate/atx-sdk';
import { getBuildInsightQuery } from './buildInsightsQueries';
import { buildsColorMap, buildTimeRange, buildHistoryTimeRange } from './constants';

// Hide g2plot's hardcoded donut-center "Total <n>" annotation (atx-sdk exposes no flag for it).
const ChartContainer = styled.div`
  ${({ $hideStat }) => ($hideStat ? '.g2-html-annotation { display: none !important; }' : '')}
`;

// Override the query's options.type only for variants that differ from what the config ships
// (e.g. a pie config rendered as a donut). 'bar' is intentionally NOT listed: BUILD_HISTORY ships
// options.type='stacked_bar_chart' and FAILURE_CATEGORIES ships 'bar_chart' — listing 'bar' here
// would clobber the stacked type and render grouped bars instead of stacked. Mirrors analytics' LegacyChartWidget.
const chartTypeToOptionsType = { donut: 'donut_chart', pie: 'pie_chart' };

// Thin wrapper over the atx-sdk widget. Ported from analytics LegacyChartWidget; build-scoped query
// is constructed locally from the widget config + the build's time range. The font-size theme patch
// the billboard needs is applied once by the parent <BuildInsights/>, not per chart.
const InsightChart = ({ widgetType, build, chartType, metadata, height = 240, customHeight = 310 }) => {
  const { query, title } = useMemo(() => {
    const config = getBuildInsightQuery({
      widgetType,
      buildId: build?.id,
      buildName: build?.name,
      timeRange: buildTimeRange(build),
      historyTimeRange: buildHistoryTimeRange(build),
    });
    if (!config) return { query: null, title: null };
    const optionsType = chartType ? chartTypeToOptionsType[chartType] : null;
    if (optionsType) {
      config.options = { ...config.options, type: optionsType };
    }
    return { query: JSON.stringify(config), title: config.name };
  }, [widgetType, build?.id, build?.name, build?.createdAt, chartType]);

  if (!query) return null;

  return (
    <ChartContainer $hideStat={chartType === 'donut'}>
      <AnalyticsComponent
        key={`${widgetType}-${chartType ?? 'default'}`}
        height={height}
        mode="widget"
        query={query}
        queryVersion="v2"
        title={title}
        useNewWidgetBox
        id={widgetType}
        customHeight={customHeight}
        colors={buildsColorMap}
        {...(chartType ? { chartType } : {})}
        {...(metadata ? { metadata } : {})}
      />
    </ChartContainer>
  );
};

// Memoized so a parent re-render (e.g. SSE/live updates churning the build page) doesn't re-render
// the atx-sdk widget and re-fetch its data while the props (build, widgetType, chartType) are stable.
export default React.memo(InsightChart);
